import { useEffect, useRef, useState } from "react";
import style from "../../booking/style.module.scss";
import { Image } from "antd";
import { CustomerRemovalControlStatusConstant } from "../core/_constant.gateOut";
import { StatusTag } from "../../../../common/components/Status/StatusTag";

interface GateOutDetailsProps {
  value?: any;
}

export const GateOutDetails = ({ value }: GateOutDetailsProps) => {
  const {
    reference = "-",
    state: { id = "-" } = {},
    createdAt = "-",

    checkedBy: {
      fullname = "-",
      email = "-",
    } = {},

    customer: {
      name: customerName = "-",
    } = {},
    finalCustomer: {
      name: finalCustomerName = "-",
    } = {},

    bookingReference = "-",
    bookingBlNumber = "-",
    uniqueIdentifierLoad = "-",

    carrierName = "-",
    forwarderAgentName = "-",
    invoiceNumber = "-",
    removalSource: {
      name: removalSourceName = "-",
    } = {},

    vehicleType: {
      id: vehicleTypeId = "-",
      name: vehicleType = "-",
    } = {},

    truck: {
      reference: truckReference = "-",
      materialFrom: {
        name: truckMaterialProvenance = "-",
      } = {},
    } = {},

    particularTruckNumber = "-",
    particularDriverName = "-",
    driverLicenseNumber = "-",
    driverIdCardNumber = "-",

    deliveryLocation = "-",
    deliveryDate = "-",
    emptyTCrecoveryDate = "-",

    containerRestitutionDelay: {
      label: planification = "-",
    } = {},

    damages = [],
    comment = "-",
  } = value || {};

  const images = value?.images ?? [];

  const imagesContainerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(images.length);
  const [showAllImages, setShowAllImages] = useState(false);
  
  useEffect(() => {
    const container = imagesContainerRef.current;
  
    if (!container || !images.length) {
      return;
    }
  
    const calculateVisibleImages = () => {
      const containerWidth = container.clientWidth;
  
      // largeur d'une image + gap
      const imageWidth = 140;
      const gap = 12;
  
      const count = Math.floor(
        (containerWidth + gap) / (imageWidth + gap)
      );
  
      setVisibleCount(Math.max(1, count));
    };
  
    calculateVisibleImages();
  
    const resizeObserver = new ResizeObserver(calculateVisibleImages);
    resizeObserver.observe(container);
  
    return () => resizeObserver.disconnect();
  }, [images.length]);

  return (
    <div className={style.details}>
      {/* Informations sur le contrôle */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Informations sur le contrôle
        </h3>

        <div className={style.details__grid}>
          <div className={style.details__item}>
            <span>Référence du contrôle</span>
            <strong>{reference}</strong>
          </div>

          <div className={style.details__item}>
            <span>Statut</span>
            {/* <strong>{name}</strong> */}
            <StatusTag
              value={id}
              options={CustomerRemovalControlStatusConstant.OPTIONS}
            />
          </div>

          <div className={style.details__item}>
            <span>Date du contrôle</span>
            <strong>{createdAt}</strong>
          </div>

          <div className={style.details__item}>
            <span>Contrôlé par</span>
            <strong>{fullname}</strong>

            {email !== "-" && (
              <small className={style.details__item__secondary}>
                {email}
              </small>
            )}
          </div>
        </div>
      </section>

      {/* Informations Booking & conteneur */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Booking & conteneur
        </h3>

        <div className={style.details__grid}>
          <div className={style.details__item}>
            <span>N° Booking</span>
            <strong>{bookingReference}</strong>
          </div>

          <div className={style.details__item}>
            <span>N° BL</span>
            <strong>{bookingBlNumber}</strong>
          </div>

          <div className={style.details__item}>
            <span>Identifiant Conteneur</span>
            <strong>{uniqueIdentifierLoad}</strong>
          </div>

          <div className={style.details__item}>
            <span>Transitaire</span>
            <strong>{customerName}</strong>
          </div>

          <div className={style.details__item}>
            <span>Client</span>
            <strong>{finalCustomerName}</strong>
          </div>
        </div>
      </section>

      {/* Informations transport & livraison */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Transport & livraison
        </h3>

        <div className={style.details__grid}>
          <div className={style.details__item}>
            <span>Type de véhicule</span>
            <strong>{vehicleType}</strong>
          </div>

          {vehicleTypeId === "INTERNAL" && (
            <>
              <div className={style.details__item}>
                <span>Camion</span>
                <strong>{truckReference}</strong>
              </div>

              <div className={style.details__item}>
                <span>Provenance</span>
                <strong>{truckMaterialProvenance}</strong>
              </div>
            </>
          )}

          {vehicleTypeId === "PARTICULAR" && (
            <>
              <div className={style.details__item}>
                <span>N° véhicule</span>
                <strong>{particularTruckNumber}</strong>
              </div>

              <div className={style.details__item}>
                <span>Conducteur</span>
                <strong>{particularDriverName}</strong>
              </div>

              <div className={style.details__item}>
                <span>N° permis de conduire</span>
                <strong>{driverLicenseNumber}</strong>
              </div>

              <div className={style.details__item}>
                <span>N° pièce d'identité</span>
                <strong>{driverIdCardNumber}</strong>
              </div>
            </>
          )}

          <div className={style.details__item}>
            <span>Lieu de livraison</span>
            <strong>{deliveryLocation}</strong>
          </div>

          <div className={style.details__item}>
            <span>Date de livraison</span>
            <strong>{deliveryDate}</strong>
          </div>

          <div className={style.details__item}>
            <span>Date prévue de récupération TC vide</span>
            <strong>{emptyTCrecoveryDate}</strong>
          </div>

          <div className={style.details__item}>
            <span>Restitution</span>
            <strong>{planification}</strong>
          </div>

          <div className={style.details__item}>
            <span>Transporteur / Client Transporteur</span>
            <strong>{carrierName}</strong>
          </div>

          <div className={style.details__item}>
            <span>Agent transitaire</span>
            <strong>{forwarderAgentName}</strong>
          </div>

          <div className={style.details__item}>
            <span>N° Facture</span>
            <strong>{invoiceNumber}</strong>
          </div>

          <div className={style.details__item}>
            <span>Lieu d'enlèvement</span>
            <strong>{removalSourceName}</strong>
          </div>
        </div>
      </section>

      {/* Avaries contatés */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Avaries constatées
        </h3>

        {damages.length > 0 ? (
          <div className={style.details__damages}>
            {damages.map((damage: any, index: number) => (
              <div
                key={damage.id ?? index}
                className={style.details__damage}
              >
                {damage.name ?? "-"}
              </div>
            ))}
          </div>
        ) : (
          <span className={style.details__empty}>
            Aucune avarie constatée.
          </span>
        )}
      </section>

      {/* Commentaires */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Commentaire
        </h3>

        {comment && comment !== "-" ? (
          <div className={style.details__comment}>
            {comment}
          </div>
        ) : (
          <span className={style.details__empty}>
            Aucun commentaire.
          </span>
        )}
      </section>

      {/* Images */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Images
        </h3>

        {images.length > 0 ? (
          <div
            ref={imagesContainerRef}
            className={`${style.details__images} ${
              showAllImages ? style.details__images__expanded : ""
            }`}
          >
            {images.map((image: any, index: number) => {
              const hasMoreImages = images.length > visibleCount;

              const isLastVisibleImage =
                !showAllImages &&
                hasMoreImages &&
                index === visibleCount - 1;

              return (
                <div
                  key={image["@id"] || index}
                  className={style.details__image}
                >
                  <Image
                    src={image.contentUrl}
                    alt={image.reference || `Image ${index + 1}`}
                    preview={!isLastVisibleImage}
                    className={
                      isLastVisibleImage
                        ? style.details__image__blurred
                        : ""
                    }
                  />

                  {isLastVisibleImage && (
                    <div
                      className={style.details__image__overlay}
                      onClick={() => setShowAllImages(true)}
                    >
                      <span>Voir toutes les images</span>
                    </div>
                  )}
                </div>
              );
            })}
            {showAllImages && images.length > visibleCount && (
        <button
          type="button"
          className={style.details__imagesToggle}
          onClick={() => setShowAllImages(false)}
        >
          Réduire les images
        </button>
      )}
          </div>
        ) : (
          <div className={style.details__empty}>
            Aucune image.
          </div>
        )}
      </section>
    </div>
  );
};